import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DivisionDefaultModal } from '../../../components/Divisions/modals/DivisionDefaultModal';
import { DivisionsDefaultTable } from '../../../components/Divisions/tables/DivisionsDefaultTable';
import { Page } from '../../../components/Page/Page';
import { useConfirm } from '../../../hooks/useConfirm';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useDeleteDivisionMutation } from '../../../types/planung-graphql-client-defs';
import { showSuccessDeleteToast, showUserDbErrorToast } from '../../../utils/toast';

export const SetupDivisions = () => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('DIVISIONS');

  const permissions = usePermissionChecker();

  const [, deleteDivisions] = useDeleteDivisionMutation();

  const { confirm, ConfirmationDialog } = useConfirm({
    defaultMessage: t('divisions.deleteDefaultMessage'),
  });

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [divisionUuid, setDivisionUuid] = useState<string | null>(null);

  function handleAdd() {
    setDivisionUuid(null);
    setModalOpen(true);
  }

  function handleClose() {
    setDivisionUuid(null);
    setModalOpen(false);
  }

  async function handleDelete(uuid: string) {
    const result = await confirm();
    if (!result) return;

    const { error } = await deleteDivisions({ uuid }, context);

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
  }

  function handleEdit(uuid: string) {
    setDivisionUuid(uuid);
    setModalOpen(true);
  }

  return (
    <Page isForbidden={!permissions?.canManageSettings()} title={t('routes.divisions.title')}>
      <DivisionsDefaultTable
        onAdd={() => handleAdd()}
        onDelete={(uuid) => handleDelete(uuid)}
        onEdit={(uuid) => handleEdit(uuid)}
      />

      <DivisionDefaultModal divisionUuid={divisionUuid} isOpen={modalOpen} onClose={() => handleClose()} />

      <ConfirmationDialog />
    </Page>
  );
};
